import React, { useCallback, useEffect, useState } from "react";
import { FaSearch, FaSuitcaseRolling, FaTaxi } from "react-icons/fa";
import Button from "../../Components/UI/Button";
import { SectionHeading } from "../../Components/UI/Card";
import MarketplaceCard from "../../Components/Marketplace/MarketplaceCard";
import Pagination from "../../Components/Marketplace/Pagination";
import { getServices, getPackages, getApiErrorMessage } from "../../Components/Marketplace/services/marketplaceApi";
import { SERVICE_CATEGORIES } from "../../Components/Marketplace/marketplaceUtils";

const LIMIT = 12;

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
];

const MarketplaceBrowsePage = () => {
  const [activeTab, setActiveTab] = useState("services"); // 'services' | 'packages'

  // Shared-ish filters
  const [q, setQ] = useState("");
  const [destination, setDestination] = useState("");
  const [category, setCategory] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: LIMIT, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: LIMIT,
        sort,
        destination: destination || undefined,
        maxPrice: maxPrice || undefined,
      };

      let res;
      if (activeTab === "services") {
        res = await getServices({
          ...params,
          category: category || undefined,
          q: q || undefined,
          minPrice: minPrice || undefined,
        });
      } else {
        res = await getPackages(params);
      }

      setItems(Array.isArray(res?.data) ? res.data : []);
      setPagination(
        res?.pagination || { page, limit: LIMIT, totalCount: (res?.data || []).length }
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load marketplace listings."));
      setItems([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, sort, destination, maxPrice, category, q, minPrice]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, category, q, minPrice, maxPrice, destination, sort]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setCategory("");
    setQ("");
    setMinPrice("");
  };

  return (
    <div className="min-h-screen bg-ink-50/40">
      {/* Hero / heading */}
      <div className="bg-gradient-to-br from-brand-600 to-brand-800 text-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-10">
          <p className="text-xs font-bold tracking-[0.14em] uppercase text-brand-100 mb-2">
            Bachatwali Ticket Marketplace
          </p>
          <h1 className="text-2xl sm:text-[32px] font-extrabold tracking-tight">
            Taxis, transfers &amp; holiday packages from verified partners
          </h1>
          <p className="mt-2 text-brand-50/90 text-sm sm:text-base max-w-2xl">
            Browse services and ready-made trips, compare prices, and book directly — no waiting on quotes.
          </p>

          {/* Services / Packages toggle */}
          <div className="mt-6 inline-flex rounded-xl bg-white/15 p-1 backdrop-blur">
            <button
              onClick={() => switchTab("services")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition ${
                activeTab === "services" ? "bg-white text-brand-700" : "text-white/90 hover:bg-white/10"
              }`}
            >
              <FaTaxi /> Services
            </button>
            <button
              onClick={() => switchTab("packages")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition ${
                activeTab === "packages" ? "bg-white text-brand-700" : "text-white/90 hover:bg-white/10"
              }`}
            >
              <FaSuitcaseRolling /> Holiday Packages
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-8">
        {/* Filters */}
        <div className="bg-white rounded-2xl border border-ink-100 shadow-[0_1px_2px_rgba(20,22,29,0.04),0_8px_24px_-8px_rgba(20,22,29,0.10)] p-4 sm:p-5 mb-6">
          {activeTab === "services" ? (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => setCategory("")}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition ${
                    category === ""
                      ? "bg-brand-600 text-white border-brand-600"
                      : "bg-white text-ink-700 border-ink-200 hover:bg-ink-50"
                  }`}
                >
                  All
                </button>
                {SERVICE_CATEGORIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition ${
                      category === c
                        ? "bg-brand-600 text-white border-brand-600"
                        : "bg-white text-ink-700 border-ink-200 hover:bg-ink-50"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <div className="relative lg:col-span-2">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 text-sm" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search services, destinations..."
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
                  />
                </div>
                <input
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  type="number"
                  min="0"
                  placeholder="Min price"
                  className="px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
                />
                <input
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  type="number"
                  min="0"
                  placeholder="Max price"
                  className="px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
                />
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="px-3 py-2.5 rounded-lg border border-ink-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <input
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="Destination (e.g. Goa)"
                className="px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
              />
              <input
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                type="number"
                min="0"
                placeholder="Max price"
                className="px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
              />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="px-3 py-2.5 rounded-lg border border-ink-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <SectionHeading
          title={activeTab === "services" ? "Available services" : "Holiday packages"}
          subtitle={
            loading
              ? "Loading..."
              : `${pagination.totalCount || 0} ${activeTab === "services" ? "service" : "package"}${
                  pagination.totalCount === 1 ? "" : "s"
                } found`
          }
          className="mb-5"
        />

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-brand-50 border border-brand-200 text-brand-700 text-sm flex items-center justify-between">
            <span>{error}</span>
            <Button variant="secondary" size="sm" onClick={fetchData}>
              Retry
            </Button>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-72 rounded-2xl bg-ink-100 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-ink-500">
            <p className="text-lg font-semibold text-ink-700 mb-1">Nothing here yet</p>
            <p className="text-sm">Try a different category, destination, or price range.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {items.map((item) => (
                <MarketplaceCard
                  key={item._id}
                  item={item}
                  type={activeTab === "services" ? "service" : "package"}
                />
              ))}
            </div>
            <Pagination
              page={pagination.page || page}
              limit={pagination.limit || LIMIT}
              totalCount={pagination.totalCount || 0}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MarketplaceBrowsePage;
